import * as Highcharts from 'highcharts';
import { number_format } from '../util';

/**
 * Empty-aware tooltip helper for Highcharts charts that emit `y: null` for
 * missing data points. Renders a date header + italic grey "No data" row for
 * empty points, or delegates value formatting to a per-chart callback.
 *
 * Two modes:
 *   - Single-series (default): used by /home alc, runs, stairmaster, reading
 *   - Shared/stacked (`config.shared === true`): used by transactions and the
 *     /transactions monthly chart in Plan 40-02
 */
export interface EmptyAwareTooltipConfig {
    shared?: boolean;
    formatValue?: (ctx: Highcharts.Point) => string;
    formatHeader?: (ctx: Highcharts.Point) => string;
    // Phase 41 forward-compat hook — declared but unused in Phase 40. Phase 41
    // (Year-over-Year overlay) will pass extraRows to append prior-year + %
    // change rows after the primary value rows.
    extraRows?: (ctx: Highcharts.Point) => string[];
    // Shared/stacked mode: formats each per-series row value AND the total
    // row. Default = money ($ + 2-decimal). Pass `y => String(y)` for
    // integer/count charts (e.g. gym) that share the stacked layout but
    // aren't currency.
    sharedValueFormatter?: (y: number) => string;
    // Shared/stacked mode: label for the total row, or `false` to omit the
    // total row entirely. Default = 'Total'.
    sharedTotalLabel?: string | false;
}

const EMPTY_HTML = '<i style="color:#999">No data</i>';

const DEFAULT_HEADER = (ctx: Highcharts.Point): string =>
    '<span style="font-size:0.8em">'
    + Highcharts.dateFormat('%b %e, %Y', ctx.x as number)
    + '</span>';

/**
 * Make a chart's tooltip + crosshair fire on x-positions where every visible
 * series has `y: null`. Highcharts excludes null points from the kdTree
 * (Pointer.findNearestKDPoint) AND filters them out of shared hover points
 * (Pointer.getHoverData line ~518: `find(s.points, p => p.x === hoverPoint.x
 * && !p.isNull)`), so the native flow can never show "No data" tooltips.
 *
 * Solution: override `pointer.getHoverData` to do a null-tolerant nearest-
 * point search keyed on the cursor's xValue. Highcharts' own runPointActions
 * then drives tooltip.refresh + drawCrosshair using our hoverPoints, with no
 * race against a competing mousemove handler.
 *
 * Call this from `chart.events.load`.
 */
export function wireNullAwareHover(
    chart: Highcharts.Chart,
    pointInterval: number = 86400000
): void {
    const pointer = chart.pointer as any;
    if (!pointer || typeof pointer.getHoverData !== 'function') {
        console.error('[emptyAwareTooltip] pointer.getHoverData not found — null-point tooltips disabled. Highcharts internal API may have changed. Re-verify Pointer.getHoverData signature after upgrade.');
        return;
    }

    // D-04 idempotency: prevent double-wrapping when re-applied on chart.events.render.
    // Option B (tagged-property sentinel) per UI-SPEC.md Override Preservation Contract.
    if (pointer._nullAwarePatched) return;

    const origGetHoverData = pointer.getHoverData.bind(pointer);

    pointer.getHoverData = function(
        existingHoverPoint: any,
        existingHoverSeries: any,
        series: any,
        isDirectTouch: any,
        shared: any,
        e: any
    ) {
        if (!e || !chart.xAxis[0]) {
            return origGetHoverData(existingHoverPoint, existingHoverSeries, series, isDirectTouch, shared, e);
        }
        const xAxis = chart.xAxis[0];
        const xValue = xAxis.toValue(e.chartX);
        const xMin = xAxis.min;
        const xMax = xAxis.max;
        if (xMin == null || xMax == null || xValue < xMin || xValue > xMax) {
            return origGetHoverData(existingHoverPoint, existingHoverSeries, series, isDirectTouch, shared, e);
        }

        // Binary search nearest point.x in the first visible series.
        const refSeries = chart.series.find(s => s.visible && s.points.length > 0);
        if (!refSeries) {
            return origGetHoverData(existingHoverPoint, existingHoverSeries, series, isDirectTouch, shared, e);
        }
        const pts = refSeries.points;
        let lo = 0, hi = pts.length - 1;
        while (lo < hi) {
            const mid = (lo + hi) >>> 1;
            if (pts[mid].x < xValue) lo = mid + 1;
            else hi = mid;
        }
        const candidates: Highcharts.Point[] = [];
        if (pts[lo - 1]) candidates.push(pts[lo - 1]);
        if (pts[lo]) candidates.push(pts[lo]);
        let closest: Highcharts.Point | null = null;
        let closestDist = Infinity;
        for (const c of candidates) {
            const d = Math.abs(c.x - xValue);
            if (d < closestDist) {
                closestDist = d;
                closest = c;
            }
        }
        if (!closest || closestDist > pointInterval) {
            return origGetHoverData(existingHoverPoint, existingHoverSeries, series, isDirectTouch, shared, e);
        }
        const snapped = closest.x;

        // Collect all visible-series points at snapped x.
        const hoverPoints: Highcharts.Point[] = [];
        for (const s of chart.series) {
            if (!s.visible) continue;
            if ((s.options as any).enableMouseTracking === false) continue;
            const p = s.points.find(pt => pt.x === snapped);
            if (p) hoverPoints.push(p);
        }
        if (hoverPoints.length === 0) {
            return origGetHoverData(existingHoverPoint, existingHoverSeries, series, isDirectTouch, shared, e);
        }

        // Null points have undefined plotX/plotY → point.pos() returns
        // undefined → tooltip.getAnchor + shouldShowTooltip fail. Pre-populate
        // axis-relative pixel coords so refresh() can position the tooltip.
        for (const p of hoverPoints) {
            const pAny = p as any;
            if (pAny.isNull || pAny.plotX == null || pAny.plotY == null) {
                const sxAxis = p.series.xAxis;
                const syAxis = p.series.yAxis;
                if (sxAxis) pAny.plotX = sxAxis.toPixels(p.x, true);
                if (syAxis) pAny.plotY = (syAxis.len ?? 0) / 2;
            }
        }

        const hoverPoint = hoverPoints[0];
        return {
            hoverPoint: hoverPoint,
            hoverSeries: hoverPoint.series,
            hoverPoints: hoverPoints,
        };
    };
    pointer._nullAwarePatched = true;
}

export function makeEmptyAwareTooltip(
    config: EmptyAwareTooltipConfig = {}
): Highcharts.TooltipFormatterCallbackFunction {
    return function(this: Highcharts.Point): string {
        const headerFn = config.formatHeader ?? DEFAULT_HEADER;
        const header = headerFn(this) + '<br/>';
        const extra = config.extraRows ? config.extraRows(this).join('') : '';

        if (config.shared) {
            // Shared/stacked path — used by transactions on /home + chart_txn_monthly
            // on /transactions in Plan 40-02. Implemented now so the helper API is
            // complete; no caller exercises it until Plan 40-02.
            const allEmpty = (this.points ?? []).every(p => p.y == null);
            if (allEmpty) return header + EMPTY_HTML + '<br/>' + extra;

            const fmtVal = config.sharedValueFormatter
                ?? ((y: number) => '$' + number_format(y, 2));
            const totalLabel = config.sharedTotalLabel ?? 'Total';
            let html = header;
            if (totalLabel !== false) {
                const total = (this.points ?? []).reduce(
                    (sum, p) => sum + (typeof p.y === 'number' ? p.y : 0),
                    0
                );
                html += `<b>${totalLabel}: ${fmtVal(total)}</b><br/>`;
            }
            const nonZero = (this.points ?? [])
                .filter(p => p.y != null && (p.y as number) > 0)
                .sort((a, b) => (b.y as number) - (a.y as number));
            for (const p of nonZero) {
                const color = typeof p.series.color === 'string'
                    ? p.series.color
                    : '#aaa';
                html += `<span style="color:${color}">●</span> `
                      + `${p.series.name}: <b>${fmtVal(p.y as number)}</b><br/>`;
            }
            return html + extra;
        }

        // Single-series path. When tooltip.shared:true is set on the chart
        // (required for null x-positions to fire hover on column/spline charts),
        // Highcharts populates this.points instead of this.y / this.point.
        // Resolve to the actual point so formatValue callbacks see the same
        // shape regardless of whether the chart uses shared:true or not.
        const pt: any = (this.points && this.points.length > 0)
            ? this.points[0]
            : this;
        const series = pt.series ?? this.series;
        const isEmpty = pt.y == null;
        const seriesColor = typeof series.color === 'string'
            ? series.color
            : '#3BABD3'; // gradient fallback matches existing alc color guard
        const ctxForFmt: any = {
            ...this,
            y: pt.y,
            point: pt,
            series: series,
        };
        const valueHtml = isEmpty
            ? EMPTY_HTML
            : (config.formatValue ? config.formatValue(ctxForFmt) : String(pt.y));
        return header
             + `<span style="color:${seriesColor}">●</span> `
             + `${series.name}: <b>${valueHtml}</b><br/>`
             + extra;
    };
}
